import React, { createContext, useContext, useEffect, useState } from 'react';

// Google User interface based on Google Identity Services
interface GoogleUser {
  id: string;
  email: string;
  name: string;
  picture: string;
  given_name: string;
  family_name: string;
}

interface AuthContextType {
  user: GoogleUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isGoogleReady: boolean;
  signIn: () => void;
  signOut: () => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Session storage key
const SESSION_KEY = 'patterniq_auth_session';
const SESSION_EXPIRY_DAYS = 7;

interface AuthSession {
  user: GoogleUser;
  expiresAt: number;
  token?: string; // JWT token from Google OAuth
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<GoogleUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGoogleReady, setIsGoogleReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = !!user;

  // DEV/PREVIEW BYPASS: Auto-login for testing (disable for production)
  const isPreviewOrDev = import.meta.env.VITE_BYPASS_AUTH === 'true' || 
                         window.location.hostname.includes('vercel.app') ||
                         window.location.hostname === 'localhost';

  // Initialize Google Sign-In on component mount
  useEffect(() => {
    // If preview/dev bypass enabled, auto-login
    if (isPreviewOrDev && !user) {
      const devUser: GoogleUser = {
        id: 'dev-user-123',
        email: 'chaz@patterniqai.com',
        name: 'Chaz (Preview)',
        picture: '',
        given_name: 'Chaz',
        family_name: 'Hatfield'
      };
      
      const devSession: AuthSession = {
        user: devUser,
        expiresAt: Date.now() + (SESSION_EXPIRY_DAYS * 24 * 60 * 60 * 1000),
        token: 'dev-bypass-token'
      };
      
      localStorage.setItem(SESSION_KEY, JSON.stringify(devSession));
      setUser(devUser);
      setIsLoading(false);
      console.log('[AUTH] Preview/Dev bypass enabled - auto-logged in');
      return;
    }

    const initializeGoogleSignIn = () => {
      if (typeof window !== 'undefined' && window.google) {
        try {
          window.google.accounts.id.initialize({
            client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
            callback: handleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: false,
            use_fedcm_for_prompt: false, // Disable FedCM to use standard OAuth popup
          });
          console.log('Google Sign-In initialized successfully');
          setIsGoogleReady(true); // Signal that Google is ready
        } catch (error) {
          console.error('Error initializing Google Sign-In:', error);
          setError('Failed to initialize Google Sign-In');
        }
      }
    };

    // Check for existing session on mount
    checkExistingSession();
    
    // Wait for Google script to load (it's already in index.html)
    if (window.google) {
      initializeGoogleSignIn();
    } else {
      // If not loaded yet, wait for it
      const checkGoogle = setInterval(() => {
        if (window.google) {
          clearInterval(checkGoogle);
          initializeGoogleSignIn();
        }
      }, 100);
      
      // Cleanup interval after 10 seconds
      setTimeout(() => clearInterval(checkGoogle), 10000);
      
      return () => clearInterval(checkGoogle);
    }
  }, []);

  const checkExistingSession = () => {
    try {
      const sessionData = localStorage.getItem(SESSION_KEY);
      if (sessionData) {
        const session: AuthSession = JSON.parse(sessionData);
        
        // Check if session is still valid
        if (session.expiresAt > Date.now()) {
          // Validate email domain
          if (validateEmailDomain(session.user.email)) {
            setUser(session.user);
          } else {
            // Clear invalid session
            localStorage.removeItem(SESSION_KEY);
          }
        } else {
          // Session expired
          localStorage.removeItem(SESSION_KEY);
        }
      }
    } catch (error) {
      console.error('Error checking existing session:', error);
      localStorage.removeItem(SESSION_KEY);
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmailDomain = (email: string): boolean => {
    const allowedDomain = import.meta.env.VITE_ALLOWED_DOMAIN || 'patterniqai.com';
    return email.toLowerCase().endsWith(`@${allowedDomain.toLowerCase()}`);
  };

  const handleCredentialResponse = (response: any) => {
    try {
      // Decode the JWT token to get user info
      const payload = JSON.parse(atob(response.credential.split('.')[1]));
      const userData: GoogleUser = {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        picture: payload.picture,
        given_name: payload.given_name,
        family_name: payload.family_name,
      };

      // Validate email domain
      if (!validateEmailDomain(userData.email)) {
        setError(`Access denied. Only @${import.meta.env.VITE_ALLOWED_DOMAIN || 'patterniqai.com'} email addresses are allowed.`);
        return;
      }

      // Create session with 7-day expiration
      const expiresAt = Date.now() + (SESSION_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
      const session: AuthSession = {
        user: userData,
        expiresAt,
        token: response.credential // Store the JWT token for API authentication
      };

      // Store session
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      
      // Update state
      setUser(userData);
      setError(null);
    } catch (error) {
      console.error('Error handling credential response:', error);
      setError('Authentication failed. Please try again.');
    }
  };

  const signIn = () => {
    if (typeof window !== 'undefined' && window.google) {
      window.google.accounts.id.prompt();
    } else {
      setError('Google Sign-In is not available. Please refresh the page.');
    }
  };

  const signOut = () => {
    // Clear session
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
    setError(null);
    
    // Revoke Google session if available
    if (typeof window !== 'undefined' && window.google) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    isGoogleReady,
    signIn,
    signOut,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Extend Window interface for Google Identity Services
declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}
